// @greenlight-spec: decisions/0026-integration-catalog-and-connection-profiles.md
// @greenlight-spec: 16-api-conventions.md
// @greenlight-spec: 28-it-dashboard-ui.md
import { z } from 'zod';
import { AuthInjectSchema, IntegrationAuthModeSchema, IntegrationDeliveryModeSchema, TokenEndpointAuthMethodSchema, } from '../schemas/api.js';
import { CatalogEntrySummarySchema } from '../schemas/integration-connection-request.js';
import { PLACEHOLDER_FORMATS, PLACEHOLDER_NORMALIZES } from './types.js';
// snake_case read projection of the integration catalog the registration picker
// reads (ADR-0026, docs/28 § /integrations). The catalog itself is an in-repo seed
// of camelCase domain types (see ./types.ts); this is the API shape, distinct from
// the domain shape the same way IntegrationListItem is distinct from the DB row.
export const CatalogUrlPlaceholderSchema = z.object({
    token: z.string(),
    label: z.string(),
    hint: z.string().nullable(),
    example: z.string().nullable(),
    normalize: z.enum(PLACEHOLDER_NORMALIZES).nullable(),
    format: z.enum(PLACEHOLDER_FORMATS).nullable(),
});
export const CatalogAuthConfigFieldSchema = z.object({
    key: z.string(),
    label: z.string(),
    hint: z.string().nullable(),
    example: z.string().nullable(),
    format: z.enum(PLACEHOLDER_FORMATS).nullable(),
});
export const CatalogSecretFieldSchema = z.object({
    key: z.string(),
    label: z.string(),
    placeholder: z.string().nullable(),
});
export const CatalogSetupGuideSchema = z.object({
    docs_label: z.string(),
    steps_title: z.string(),
    steps: z.array(z.object({ title: z.string(), body: z.string(), details: z.string().nullable() })),
    statement: z.object({ title: z.string(), template: z.string() }).nullable(),
});
export const CatalogCredentialRoleSchema = z.object({
    label: z.string(),
    hint: z.string(),
    example: z.string(),
    blocked: z.array(z.string()),
});
export const CatalogConnectionProfileSchema = z.object({
    id: z.string(),
    auth_mode: IntegrationAuthModeSchema,
    mechanism: z.string().nullable(),
    // The wire placement for a static credential (service_account); null otherwise.
    auth_inject: AuthInjectSchema.nullable(),
    credential_kind: z.string(),
    // OAuth2 endpoint + scopes prefill: token_url for client_credentials and per-user
    // OAuth, authorize_url for per-user OAuth only; null otherwise.
    token_url: z.string().nullable(),
    authorize_url: z.string().nullable(),
    token_endpoint_auth_method: TokenEndpointAuthMethodSchema.nullable(),
    scopes: z.array(z.string()).nullable(),
    acquisition_docs_url: z.string(),
    acquisition_instructions: z.string(),
    display_name: z.string(),
    client_id_label: z.string().nullable(),
    client_id_placeholder: z.string().nullable(),
    secret_placeholder: z.string().nullable(),
    collect_mint_scopes: z.boolean(),
    credential_role: CatalogCredentialRoleSchema.nullable(),
    auth_config_fields: z.array(CatalogAuthConfigFieldSchema),
    // The structured secret's named fields, one write-only input each; empty for a
    // single opaque secret.
    secret_fields: z.array(CatalogSecretFieldSchema),
    acts_as_signed_in_user: z.boolean(),
    setup_guide: CatalogSetupGuideSchema.nullable(),
    is_default: z.boolean(),
    mvp_selectable: z.boolean(),
});
export const CatalogEntrySchema = z.object({
    catalog_key: z.string(),
    display_name: z.string(),
    category: z.string(),
    // Literal or a `{placeholder}` the registration form prompts for; empty for a
    // connected-database entry (no HTTP base URL — ADR-0030).
    base_url: z.string(),
    placeholders: z.array(CatalogUrlPlaceholderSchema),
    // Extra terms the picker's search matches (product names behind a vendor name).
    search_keywords: z.array(z.string()),
    logo_ref: z.string(),
    proxyable: z.boolean(),
    // Set for a connected-database entry: the DB engine and default port the form
    // prefills (ADR-0030); null for HTTP integrations.
    engine: z.string().nullable(),
    default_port: z.number().int().nullable(),
    // The delivery modes the entry offers (derived); a connected database is
    // `proxied`-only, a non-proxyable HTTP entry `injected`-only.
    delivery_modes: z.array(IntegrationDeliveryModeSchema),
    // The read-only path a credential test calls by default; null when the entry names none.
    probe_path: z.string().nullable().optional(),
    connection_profiles: z.array(CatalogConnectionProfileSchema),
});
/**
 * The catalog read serves two audiences from one route. IT gets the full entry —
 * base URLs, placeholders, connection profiles: the mechanics its registration
 * wizard needs. A citizen developer's agent gets only enough to name a system it
 * wants connected (ADR-0026 §7). `projection` is on the wire rather than inferred
 * from the caller's role so a client narrows explicitly and a trimmed response can
 * never be mistaken for a full one that happens to be missing fields.
 */
export const CatalogListResponseSchema = z.discriminatedUnion('projection', [
    z.object({ projection: z.literal('full'), entries: z.array(CatalogEntrySchema) }),
    z.object({ projection: z.literal('summary'), entries: z.array(CatalogEntrySummarySchema) }),
]);
//# sourceMappingURL=schema.js.map